/**
 * Cart Membership Pricing System
 * Applies live membership discounts to cart displays
 */
class CartMembershipPricingCore {
  static DISCOUNT_TIERS = {
    'bronze': 5,
    'silver': 10, 
    'gold': 15,
    'trade': 20
  };
  
  static getCustomerTier() {
    // Read customer tags from body dataset, with a resilient localStorage fallback
    let customerTags = (document.body.dataset.customerTags || '').trim();
    if (customerTags) {
      try {
        localStorage.setItem('exposurepack_customer_tags', customerTags);
      } catch (e) {
        // Ignore storage errors
      }
    } else {
      try {
        customerTags = (localStorage.getItem('exposurepack_customer_tags') || '').trim();
      } catch (e) {
        customerTags = '';
      }
    }

    const tags = customerTags
      ? customerTags.toLowerCase().split(',').map(tag => tag.trim()).filter(Boolean)
      : [];
    
    if (tags.includes('trade')) return 'trade';
    if (tags.includes('gold')) return 'gold';
    if (tags.includes('silver')) return 'silver';
    if (tags.includes('bronze')) return 'bronze';
    
    return null;
  }
  
  static getDiscountPercent() {
    const tier = this.getCustomerTier();
    return tier ? this.DISCOUNT_TIERS[tier] : 0;
  }

  // Map discount percentage back to a tier name as a fallback when tier string is unavailable
  static getTierFromDiscountPercent(percent) {
    switch (percent) {
      case 20: return 'trade';
      case 15: return 'gold';
      case 10: return 'silver';
      case 5: return 'bronze';
      default: return 'silver';
    }
  }
  
  static isProductExcludedFromDiscount(cartItem) {
    // Check if this specific cart item should be excluded from discounts
    const productTitle = cartItem.querySelector('.cart-item__name, .product-option dt')?.textContent?.toLowerCase() || '';
    const cartItemLink = cartItem.querySelector('a')?.href?.toLowerCase() || '';

    // Only exclude Printing Plate Fees from discounts in cart
    const isPlateFee =
      productTitle.includes('printing plate') ||
      productTitle.includes('plate fee') ||
      cartItemLink.includes('printing-plate');

    if (isPlateFee) {
      console.log(`[Cart Membership] Excluding item from discount: ${productTitle} (plate fee)`);
      return true;
    }

    return false;
  }
  
  static formatPriceWithDiscount(originalPriceCents, tierName, showOriginal = true) {
    const discountPercent = this.DISCOUNT_TIERS[tierName] || 0;
    const discountedPriceCents = originalPriceCents * (1 - discountPercent / 100);
    
    // Format to money display
    const originalPrice = (originalPriceCents / 100).toFixed(2);
    const discountedPrice = (discountedPriceCents / 100).toFixed(2);
    
    if (showOriginal) {
      return `
        <span class="original-price">$${originalPrice}</span>
        <span class="member-price member-${tierName}">$${discountedPrice}</span>
      `;
    } else {
      return `<span class="member-price member-${tierName}">$${discountedPrice}</span>`;
    }
  }
  
  // When a real Shopify discount (automatic, code, or per-line allocation) is active,
  // Shopify's rendered numbers (cart.total_price / item.final_line_price) are authoritative
  // and equal what checkout charges. Custom membership pricing must defer entirely so the
  // drawer/cart totals reconcile to checkout instead of being re-discounted on top.
  static shouldDeferToShopify() {
    return typeof window.epkHasShopifyDiscount === 'function' &&
           window.epkHasShopifyDiscount(window.__epkLastCart);
  }

  static applyCartDiscounts() {
    if (this.shouldDeferToShopify()) {
      console.log('[Cart Membership] Shopify discount active — deferring to Shopify prices, skipping member overwrite');
      return;
    }

    const tier = this.getCustomerTier();
    const discountPercent = this.getDiscountPercent();

    console.log(`[Cart Membership] Processing cart - Tier: ${tier || 'none'}, Discount: ${discountPercent}%`);
    
    // Always try to find and process cart price elements
    const priceSelectors = [
      '.cart-item__totals .price',
      '.drawer__contents .price',
      '.cart-drawer .price',
      'cart-drawer .price',
      '.price:not(.original-price):not(.member-price)',
      '[data-price]'
    ];
    
    let foundPrices = 0;
    priceSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      console.log(`[Cart Membership] Selector "${selector}" found ${elements.length} elements`);
      foundPrices += elements.length;
    });
    
    console.log(`[Cart Membership] Total price elements found: ${foundPrices}`);
    
    if (tier && discountPercent > 0) {
      // Apply discounts to cart drawer items
      this.applyCartDrawerDiscounts(tier, discountPercent);
      
      // Apply discounts to main cart page items
      this.applyMainCartDiscounts(tier, discountPercent);
      
      // Force update all price elements we can find
      this.forceUpdateAllPrices(tier, discountPercent);
    } else {
      console.log('[Cart Membership] No membership tier found - cart will show regular prices');
    }
    
    // Always update cart totals (crucial for consistency)
    this.updateCartTotals(tier, discountPercent);
  }
  
  static applyCartDrawerDiscounts(tier, discountPercent) {
    // Target cart drawer price elements
    const cartDrawerItems = document.querySelectorAll('cart-drawer .cart-item, .drawer__contents .cart-item');
    
    cartDrawerItems.forEach(item => {
      // Skip if already processed
      if (item.dataset.membershipProcessed) return;
      
      // Check if this item should be excluded from discounts
      if (this.isProductExcludedFromDiscount(item)) {
        console.log(`[Cart Drawer] Skipping discount for excluded product`);
        item.dataset.membershipProcessed = 'true';
        return;
      }
      
      // Find price elements in cart drawer (support multiple theme variants)
      const priceElements = item.querySelectorAll(
        '.cart-item__totals .price:not(.original-price):not(.member-price),\
         .cart-item__totals [data-cart-item-price]:not(.original-price):not(.member-price),\
         .cart-item__totals .cart-item__final-price:not(.original-price):not(.member-price),\
         .cart-item__totals .cart-item__price:not(.original-price):not(.member-price)'
      );
      
      priceElements.forEach(priceEl => {
        // Skip if this node already contains member/original spans or was processed
        if (priceEl.querySelector('.member-price, .original-price')) return;
        if (priceEl.dataset.membershipProcessed === 'true') return;
        if (priceEl.closest('.cart-item__discounted-prices')) return; // Skip already discounted items
        
        const priceText = priceEl.textContent.trim();
        const priceMatch = priceText.match(/\$?([\d,]+\.?\d{0,2})/);
        
        if (priceMatch) {
          const originalPrice = parseFloat(priceMatch[1].replace(/,/g, ''));
          const originalPriceCents = Math.round(originalPrice * 100);
          
          console.log(`[Cart Drawer] Processing price: $${originalPrice} for ${tier} member`);
          
          priceEl.innerHTML = this.formatPriceWithDiscount(originalPriceCents, tier, true);
          priceEl.dataset.membershipProcessed = 'true';
        }
      });
      
      item.dataset.membershipProcessed = 'true';
    });
  }
  
  static applyMainCartDiscounts(tier, discountPercent) {
    // Target main cart page price elements
    const cartItems = document.querySelectorAll('.cart-item:not([data-membership-processed])');
    
    cartItems.forEach(item => {
      // Check if this item should be excluded from discounts
      if (this.isProductExcludedFromDiscount(item)) {
        console.log(`[Main Cart] Skipping discount for excluded product`);
        item.dataset.membershipProcessed = 'true';
        return;
      }
      
      // Do NOT touch product option rows (like Price/Unit) to avoid duplicate display
      
      // Process main item total price (broad selectors for variants)
      const totalPriceElements = item.querySelectorAll(
        '.cart-item__totals .price:not(.original-price):not(.member-price),\
         .cart-item__totals [data-cart-item-price]:not(.original-price):not(.member-price),\
         .cart-item__totals .cart-item__final-price:not(.original-price):not(.member-price),\
         .cart-item__totals .cart-item__price:not(.original-price):not(.member-price)'
      );
      
      totalPriceElements.forEach(priceEl => {
        // Skip if this node already contains member/original spans or was processed
        if (priceEl.querySelector('.member-price, .original-price')) return;
        if (priceEl.dataset.membershipProcessed === 'true') return;
        if (priceEl.closest('.cart-item__discounted-prices')) return; // Skip already discounted
        
        const priceText = priceEl.textContent.trim();
        const priceMatch = priceText.match(/\$?([\d,]+\.?\d{0,2})/);
        
        if (priceMatch) {
          const originalPrice = parseFloat(priceMatch[1].replace(/,/g, ''));
          const originalPriceCents = Math.round(originalPrice * 100);
          
          console.log(`[Main Cart Total] Processing total: $${originalPrice} for ${tier} member`);
          
          priceEl.innerHTML = this.formatPriceWithDiscount(originalPriceCents, tier, true);
          priceEl.dataset.membershipProcessed = 'true';
        }
      });
      
      item.dataset.membershipProcessed = 'true';
    });
  }
  
  static updateCartTotals(tier, discountPercent) {
    // Defer to Shopify's authoritative cart.total_price whenever a real discount is present;
    // never overwrite the subtotal/estimated total with re-scraped membership math in that case.
    if (this.shouldDeferToShopify()) {
      console.log('[Cart Membership] Shopify discount active — leaving Shopify cart total intact');
      return;
    }

    // ALWAYS compute from real cart line data so every line (cup bundle cards, hidden/
    // grouped rush + lid lines) is included. The old DOM scrape missed bundle-card prices
    // (rendered in .epk-cart-order-line__price, not .cart-item__totals) and collapsed the
    // total to whatever standalone lines it could see. If the cart object isn't cached yet,
    // fetch it once, cache it, and re-run — never fall back to partial DOM math.
    const cartData = window.__epkLastCart;
    if (!cartData || !Array.isArray(cartData.items)) {
      if (!this._fetchingCartForTotals) {
        this._fetchingCartForTotals = true;
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (c) {
            window.__epkLastCart = c;
            CartMembershipPricingCore._fetchingCartForTotals = false;
            CartMembershipPricingCore.updateCartTotals(tier, discountPercent);
          })
          .catch(function () { CartMembershipPricingCore._fetchingCartForTotals = false; });
      }
      return;
    }

    let finalTotal = 0;
    let originalTotal = 0;
    let hasAnyDiscounts = false;

    const subtotalElements = document.querySelectorAll(
      '.totals__subtotal-value, .cart__total .price, [data-cart-subtotal], .drawer__footer .totals__subtotal-value'
    );
    const totalElements = document.querySelectorAll(
      '.totals__total-value:not(.member-price), .cart__checkout .price:not(.member-price), [data-cart-total]:not(.member-price), .drawer__footer .totals__total-value:not(.member-price)'
    );

    cartData.items.forEach(function (item) {
      var priceCents = typeof item.final_line_price === 'number' ? item.final_line_price : 0;
      var title = (item.title || item.product_title || '').toLowerCase();
      var isPlateFee = title.indexOf('printing plate') !== -1 || title.indexOf('plate fee') !== -1;
      originalTotal += priceCents / 100;
      if (isPlateFee || !tier || !discountPercent) {
        finalTotal += priceCents / 100;
      } else {
        // Match the Liquid rounding contract: round(cents * (100 - pct) / 100)
        var discounted = Math.round(priceCents * (100 - discountPercent) / 100) / 100;
        finalTotal += discounted;
        if (discounted < priceCents / 100) hasAnyDiscounts = true;
      }
    });
    console.log(`[Cart Totals] From cart API — Original: $${originalTotal.toFixed(2)}, Final: $${finalTotal.toFixed(2)}`);

    const tierName = tier || this.getCustomerTier() || this.getTierFromDiscountPercent(discountPercent);

    // Write to .custom-pricing-total (cart page footer — dedicated membership total slot)
    const customTotalEl = document.querySelector('.custom-pricing-total');
    if (customTotalEl && finalTotal > 0) {
      if (hasAnyDiscounts && originalTotal > finalTotal) {
        customTotalEl.innerHTML = `<s class="price price--end">$${originalTotal.toFixed(2)}</s> <span class="price price--end member-price member-${tierName}">$${finalTotal.toFixed(2)}</span>`;
      } else {
        customTotalEl.innerHTML = `<span class="price price--end">$${finalTotal.toFixed(2)}</span>`;
      }
    }

    // Update drawer and other total elements; never touch the Shopify-managed element
    [...subtotalElements, ...totalElements].forEach(element => {
      if (element.classList.contains('shopify-pricing-total')) return;
      if (element.querySelector('.member-price') || element.querySelector('.original-price')) return;
      if (finalTotal > 0) {
        if (hasAnyDiscounts && originalTotal > finalTotal) {
          element.innerHTML = `<span class="original-price">$${originalTotal.toFixed(2)}</span><span class="member-price member-${tierName}">$${finalTotal.toFixed(2)}</span>`;
        } else {
          element.innerHTML = `$${finalTotal.toFixed(2)}`;
        }
      }
    });
  }
  
  static forceUpdateAllPrices(tier, discountPercent) {
    console.log(`[Cart Membership] Force updating line-item price elements for ${tier} member`);
    
    // Restrict to line-item totals only; never touch cart totals here
    const allPriceElements = document.querySelectorAll(
      '.cart-item__totals .price,\
       .cart-item__totals [data-cart-item-price],\
       .cart-item__totals .cart-item__final-price,\
       .cart-item__totals .cart-item__price'
    );
    
    allPriceElements.forEach((element, index) => {
      // Skip if already processed or already contains member/original spans
      if (element.querySelector('.member-price, .original-price')) {
        return;
      }
      if (element.dataset.membershipProcessed) return;

      // Skip elements that are inside the totals area to avoid cart-wide discounting
      if (element.closest('.totals')) return;
      
      // Check if this element is within an excluded cart item
      const cartItem = element.closest('.cart-item');
      if (cartItem && this.isProductExcludedFromDiscount(cartItem)) {
        console.log(`[Force Update ${index}] Skipping excluded product price`);
        element.dataset.membershipProcessed = 'true';
        return;
      }
      
      const text = element.textContent.trim();
      const priceMatch = text.match(/\$?([\d,]+\.?\d{0,2})/);
      
      if (priceMatch) {
        const originalPrice = parseFloat(priceMatch[1].replace(/,/g, ''));
        if (originalPrice > 0) {
          const originalPriceCents = Math.round(originalPrice * 100);
          
          console.log(`[Force Update ${index}] Line item price: $${originalPrice}`);
          
          if (tier && discountPercent > 0) {
            element.innerHTML = this.formatPriceWithDiscount(originalPriceCents, tier, true);
          }
          element.dataset.membershipProcessed = 'true';
        }
      }
    });
  }
  
  static ensureCartTotalsCorrect() {
    // Standalone method to always ensure cart totals are accurate
    // This can be called from any page to fix totals
    console.log('[Cart Membership] Ensuring cart totals are correct');

    // Defer to Shopify when a real discount is active (this path also re-applies line discounts).
    if (this.shouldDeferToShopify()) {
      console.log('[Cart Membership] Shopify discount active — not re-applying member totals');
      return;
    }

    const tier = this.getCustomerTier();
    const discountPercent = tier ? this.getDiscountPercent() : 0;
    
    // Make sure all cart items are properly processed first
    if (tier && discountPercent > 0) {
      // Re-apply discounts to ensure everything is up to date
      this.applyCartDrawerDiscounts(tier, discountPercent);
      this.applyMainCartDiscounts(tier, discountPercent);
    }
    
    // Always update totals regardless of membership status
    this.updateCartTotals(tier, discountPercent);
    
    console.log('[Cart Membership] Cart totals verification complete');
  }
  
  static addMembershipIndicator() {
    const tier = this.getCustomerTier();
    if (!tier) return;
    
    const discountPercent = this.getDiscountPercent();
    
    // Use a unique ID to prevent duplicates and track if we've already added one
    const INDICATOR_ID = 'cart-membership-indicator-unique';
    
    // Remove any existing indicators first to prevent duplicates
    const existingIndicator = document.getElementById(INDICATOR_ID);
    if (existingIndicator) {
      console.log('[Cart Membership] Indicator already exists, skipping duplicate');
      return;
    }
    
    // Also remove any old-style indicators without ID
    document.querySelectorAll('.cart-membership-indicator').forEach(indicator => {
      if (!indicator.id) {
        indicator.remove();
      }
    });
    
    // Priority order: when empty, place inside the empty state container so the badge stays at the very top.
    // Otherwise, fall back to cart drawer containers.
    const possibleContainers = [
      '.drawer__inner-empty',
      '.drawer__contents',
      '.drawer__inner',
      '.cart-items',
      '.main-cart'
    ];
    
    let targetContainer = null;
    for (const selector of possibleContainers) {
      targetContainer = document.querySelector(selector);
      if (targetContainer) {
        console.log(`[Cart Membership] Found container: ${selector}`);
        break;
      }
    }
    
    if (targetContainer) {
      const indicator = document.createElement('div');
      indicator.id = INDICATOR_ID;
      indicator.className = `cart-membership-indicator membership-${tier}`;
      indicator.innerHTML = `
        <div class="membership-status">
          <span class="membership-badge membership-${tier}">
            ${tier === 'bronze' ? '🥉' : tier === 'silver' ? '🥈' : tier === 'gold' ? '🥇' : '💎'}
          </span>
          <span class="membership-text">${tier.toUpperCase()} Member - ${this.getDiscountPercent()}% OFF applied!</span>
        </div>
      `;
      // Ensure the indicator renders at the top of the chosen container
      targetContainer.insertBefore(indicator, targetContainer.firstChild);
      console.log(`[Cart Membership] Added unique membership indicator to ${targetContainer.className}`);
    } else {
      console.log('[Cart Membership] No suitable container found for indicator');
    }
  }
  
  static init() {
    // Skip on admin order pages
    if (document.getElementById('order-detail-root')) {
      console.debug('[Cart Membership] Admin order page; skipping.');
      return;
    }
    console.log('[Cart Membership] Initializing cart membership pricing system');
    console.log('[Cart Membership] Customer tags from body:', document.body.dataset.customerTags);
    console.log('[Cart Membership] Customer tier detected:', this.getCustomerTier());
    console.log('[Cart Membership] Discount percent:', this.getDiscountPercent());
    
    // Always process cart regardless of page - no more blocking
    
    // Check if we can find cart items
    const cartItems = document.querySelectorAll('.cart-item, cart-drawer .cart-item, .drawer__contents .cart-item');
    console.log('[Cart Membership] Found cart items:', cartItems.length);
    
    // Apply discounts immediately (or process cart without discounts)
    this.applyCartDiscounts();
    
    // Add membership indicator (only if customer has tier)
    const tier = this.getCustomerTier();
    if (tier) {
      this.addMembershipIndicator();
    }
    
    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      console.log('[Cart Membership] Cart updated, reapplying discounts');
      setTimeout(() => {
        // Reset processed flags
        document.querySelectorAll('[data-membership-processed]').forEach(el => {
          delete el.dataset.membershipProcessed;
        });
        
        this.applyCartDiscounts();
        // Always ensure totals are correct
        this.ensureCartTotalsCorrect();
        // Only re-add indicator if it doesn't exist and customer has tier
        const tier = this.getCustomerTier();
        if (tier && !document.getElementById('cart-membership-indicator-unique')) {
          this.addMembershipIndicator();
        }
      }, 100); // Small delay to ensure DOM is updated
    });
    
    // Listen for cart drawer opening
    document.addEventListener('cart-drawer:open', () => {
      console.log('[Cart Membership] Cart drawer opened, applying discounts');
      setTimeout(() => {
        this.applyCartDiscounts();
        this.ensureCartTotalsCorrect();
        // Only add indicator if it doesn't exist and customer has tier
        const tier = this.getCustomerTier();
        if (tier && !document.getElementById('cart-membership-indicator-unique')) {
          this.addMembershipIndicator();
        }
        // Ensure any discount popup remains visible (do not focus-steal or re-render drawer)
        const discountPopup = document.querySelector('.cart-discount-popup, [data-discount-popup]');
        if (discountPopup) {
          discountPopup.style.display = '';
          discountPopup.classList.remove('hidden');
        }
      }, 200);
    });
    
    // Observe DOM changes for dynamic cart updates
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          // Check if cart items were added/removed
          const addedNodes = Array.from(mutation.addedNodes);
          const removedNodes = Array.from(mutation.removedNodes);
          
          if (addedNodes.some(node => 
            node.nodeType === 1 && (
              node.classList?.contains('cart-item') || 
              node.querySelector?.('.cart-item') ||
              node.classList?.contains('price')
            )
          ) || removedNodes.some(node => 
            node.nodeType === 1 && (
              node.classList?.contains('cart-item') || 
              node.querySelector?.('.cart-item')
            )
          )) {
            shouldUpdate = true;
          }
        }
      });
      
      if (shouldUpdate) {
        console.log('[Cart Membership] DOM changes detected, reapplying discounts');
        setTimeout(() => this.applyCartDiscounts(), 50);
      }
    });
    
    // Observe cart containers
    const cartContainers = document.querySelectorAll(
      'cart-drawer, .cart-drawer, .cart-items, .main-cart, .drawer__contents'
    );
    
    cartContainers.forEach(container => {
      if (container) {
        observer.observe(container, {
          childList: true,
          subtree: true
        });
      }
    });
    
    console.log('🛒 Cart Membership Pricing System initialized');
    console.log('Customer tier:', this.getCustomerTier());
    console.log('Discount percent:', this.getDiscountPercent() + '%');
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  console.log('[Cart Membership] DOM ready - initializing');
  (window.CartMembershipPricing || CartMembershipPricingCore).init();
});

// Also initialize on page load in case of timing issues
window.addEventListener('load', function() {
  console.log('[Cart Membership] Window loaded - initializing');
  (window.CartMembershipPricing || CartMembershipPricingCore).init();
});

// Listen for cart drawer clicks specifically
document.addEventListener('click', function(e) {
  if (e.target.closest('[href*="cart"], [data-cart], .cart-count-bubble, .cart-icon')) {
    console.log('[Cart Membership] Cart opened via click - will apply pricing');
    setTimeout(() => {
      (window.CartMembershipPricing || CartMembershipPricingCore).init();
      (window.CartMembershipPricing || CartMembershipPricingCore).ensureCartTotalsCorrect();
    }, 500);
  }
});

// Try initialization every 2 seconds for the first 10 seconds (in case of timing issues)
let cartMembershipInitAttempts = 0;
const cartMembershipInitInterval = setInterval(() => {
  cartMembershipInitAttempts++;
  console.log(`[Cart Membership] Retry initialization attempt ${cartMembershipInitAttempts}`);
  (window.CartMembershipPricing || CartMembershipPricingCore).init();
  
  if (cartMembershipInitAttempts >= 5) {
    clearInterval(cartMembershipInitInterval);
    console.log('[Cart Membership] Stopped retry attempts');
  }
}, 2000);

// Make it globally available
window.CartMembershipPricing = window.CartMembershipPricing || CartMembershipPricingCore;
